// Prefetch helpers — warm the browser HTTP cache so clicking a card feels
// instant. Fire-and-forget; failures are silent. Guarded by a Set so the
// same id only prefetches once per page session.

import { api } from "./api";
import { getToken } from "./auth";

interface BookMinimal {
  id: string;
  status: string;
  pdf_url?: string | null;
  pdf_url_kind?: "s3" | "local" | null;
}

const seen = new Set<string>();

export function prefetchBook(bookId: string, likelyComplete = true): void {
  if (seen.has(bookId)) return;
  seen.add(bookId);

  // 1) Warm the detail payload. The response carries pdf_url → we can chain
  //    a prefetch of the actual PDF bytes too.
  api.get<BookMinimal>(`/api/books/${bookId}`)
    .then((b) => {
      if (!likelyComplete || !b.pdf_url) return;
      // 2) Kick off a low-priority GET for the PDF. We don't read the body;
      //    the browser will hold the bytes in its HTTP cache so pdfjs's
      //    ranged fetches on actual open hit local storage.
      prefetchUrl(b.pdf_url, b.pdf_url_kind === "local");
    })
    .catch(() => {
      // Intentionally silent — prefetch failures must never surface.
    });
}

function prefetchUrl(url: string, sameOrigin: boolean): void {
  // Using fetch() with { priority: "low" } when available gives the
  // browser permission to deprioritise; falls back to a vanilla request.
  try {
    const init: RequestInit = { method: "GET", credentials: sameOrigin ? "same-origin" : "omit" };
    if (sameOrigin) {
      init.headers = { Authorization: `Bearer ${getToken() ?? ""}` };
    }
    (init as RequestInit & { priority?: string }).priority = "low";
    void fetch(url, init);
  } catch {
    /* ignore */
  }
}
